import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminAPI } from '@/lib/api';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle, Clock, Download, Search, X } from 'lucide-react';

const EMPRESAS = [
  { key: 'SPC', label: 'SPC Brasil' },
  { key: 'SERASA', label: 'Serasa' },
  { key: 'BOA_VISTA', label: 'Boa Vista' },
  { key: 'CENPROT', label: 'Cenprot BR' },
];

export default function AdminLoteDetail() {
  const { loteId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [baixaLoading, setBaixaLoading] = useState('');
  // Baixa modal
  const [baixaEmpresa, setBaixaEmpresa] = useState(null);
  const [baixaObs, setBaixaObs] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.loteDetalhe(loteId);
      setData(res.data);
    } catch { toast.error('Erro ao carregar lote'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, [loteId]);

  const handleBaixarTodos = async (empresa) => {
    setBaixaLoading(empresa);
    try {
      const res = await adminAPI.baixarTodosEmpresa(loteId, { empresa, dataBaixa: new Date().toISOString(), observacao: baixaObs, ignoreAlreadyBaixado: true });
      toast.success(res.data.message);
      setBaixaEmpresa(null);
      setBaixaObs('');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
    finally { setBaixaLoading(''); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="rn-spinner" style={{width:28,height:28}} /></div>;
  if (!data) return <div className="text-center py-20 text-white/40">Lote nao encontrado</div>;

  const filteredClients = data.clientes?.filter(c =>
    !search || (c.nome + c.cpf + c.email).toLowerCase().includes(search.toLowerCase())
  ) || [];

  const isProtocolada = data.status === 'protocolada' || data.statusNorm === 'protocolada';

  return (
    <div className="space-y-5" data-testid="admin-lote-detail">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/admin/returns')} className="p-2 rounded-xl hover:bg-white/5"><ArrowLeft size={20} /></button>
        <div className="flex-1">
          <h1 className="font-display text-2xl font-semibold">{data.name || `Lote ${loteId.slice(0,8)}`}</h1>
          <div className="flex items-center gap-3 mt-1">
            <span className={`text-[10px] px-2 py-0.5 rounded-full ${data.status === 'protocolada' ? 'badge-success' : data.status === 'enviada' || data.status === 'sent' ? 'badge-warning' : 'badge-neutral'}`}>
              {data.status === 'protocolada' ? 'Protocolada' : data.status === 'enviada' || data.status === 'sent' ? 'Enviada' : data.status}
            </span>
            {data.protocoloNumero && <span className="text-xs text-white/40">Protocolo: {data.protocoloNumero}</span>}
            <span className="text-xs text-white/30">{data.totalClientes} clientes</span>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/export-lists/${loteId}/download-xlsx`}
              className="text-xs px-3 py-1 rounded-lg hover:bg-white/5 ml-2 flex items-center gap-1" style={{color:'var(--rn-green)'}}>
              <Download size={12} /> Exportar XLSX
            </a>
          </div>
        </div>
      </div>

      {/* Progress + Metrics */}
      <div className="glass-card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-white/70">Progresso de Baixas</h3>
          <span className="font-display text-xl font-bold" style={{color: data.progresso >= 100 ? 'var(--rn-green)' : 'var(--rn-warning)'}}>{data.progresso}%</span>
        </div>
        <div className="h-2.5 rounded-full bg-white/10 overflow-hidden mb-4">
          <div className="h-full rounded-full" style={{
            width: `${data.progresso}%`,
            background: data.progresso >= 100 ? 'var(--rn-green)' : 'var(--rn-warning)',
            transition: 'width 0.6s ease',
          }} />
        </div>
        <div className="flex items-center gap-2 text-xs text-white/40">
          <span>{data.totalBaixadas} / {data.totalEsperado} baixas registradas</span>
        </div>
      </div>

      {/* Baixa em Lote por Empresa */}
      {isProtocolada && (
        <div className="glass-card">
          <h3 className="text-sm font-semibold text-white/70 mb-3">Baixa em Lote por Empresa</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {EMPRESAS.map(emp => {
              // Count how many already baixado for this empresa
              const baixados = data.clientes?.filter(c => c.baixas?.[emp.key]?.status === 'baixado').length || 0;
              const total = data.totalClientes || 0;
              const allDone = baixados >= total;
              return (
                <button key={emp.key} onClick={() => !allDone && setBaixaEmpresa(emp.key)}
                  disabled={allDone || !!baixaLoading}
                  className={`p-3 rounded-xl border text-left transition-all ${
                    allDone ? 'border-[rgba(99,203,182,0.3)] bg-[rgba(99,203,182,0.06)]' : 'border-white/10 bg-white/[0.02] hover:border-[rgba(99,203,182,0.2)] hover:bg-white/[0.04]'
                  } ${!!baixaLoading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <p className="text-xs font-medium text-white/80 mb-1">{emp.label}</p>
                  <div className="flex items-center gap-1">
                    {allDone ? <CheckCircle size={12} style={{color:'var(--rn-green)'}} /> : <Clock size={12} className="text-white/30" />}
                    <span className={`text-[10px] ${allDone ? 'text-[var(--rn-green)]' : 'text-white/40'}`}>{baixados}/{total}</span>
                  </div>
                  {!allDone && <p className="text-[9px] mt-1" style={{color:'var(--rn-green)'}}>Baixar todos</p>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Baixa Confirm Modal */}
      {baixaEmpresa && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center" onClick={() => setBaixaEmpresa(null)}>
          <div className="bg-[#111817] border border-white/10 rounded-2xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <h3 className="font-display text-base font-semibold mb-3">
              Baixar TODOS — {EMPRESAS.find(e => e.key === baixaEmpresa)?.label}
            </h3>
            <p className="text-xs text-white/50 mb-3">{data.totalClientes} clientes serao marcados como baixados.</p>
            <textarea value={baixaObs} onChange={e => setBaixaObs(e.target.value)} placeholder="Observacao (opcional)" rows={2}
              className="w-full bg-black/20 border border-white/10 text-white text-sm placeholder:text-white/30 rounded-xl px-4 py-2.5 mb-3 focus:outline-none focus:ring-2 focus:ring-[rgba(99,203,182,0.3)]" />
            <div className="flex gap-3">
              <button onClick={() => setBaixaEmpresa(null)} className="btn-ghost flex-1 text-sm">Cancelar</button>
              <button onClick={() => handleBaixarTodos(baixaEmpresa)} disabled={!!baixaLoading}
                className="btn-primary flex-1 text-sm disabled:opacity-50">
                {baixaLoading === baixaEmpresa ? <span className="rn-spinner mx-auto" style={{width:14,height:14}} /> : 'Confirmar Baixa'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar cliente..."
          className="w-full bg-black/20 border border-white/10 text-white text-sm placeholder:text-white/30 rounded-xl pl-10 pr-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[rgba(99,203,182,0.3)]" />
      </div>

      {/* Clients Table */}
      <div className="glass-card !p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px] text-sm">
            <thead>
              <tr className="border-b border-white/10 bg-white/[0.02]">
                <th className="text-left text-[10px] text-white/40 font-medium py-2.5 px-3">NOME</th>
                <th className="text-left text-[10px] text-white/40 font-medium py-2.5 px-3">CPF</th>
                {EMPRESAS.map(e => (
                  <th key={e.key} className="text-center text-[10px] text-white/40 font-medium py-2.5 px-2">{e.label.toUpperCase()}</th>
                ))}
                <th className="text-center text-[10px] text-white/40 font-medium py-2.5 px-2">STATUS</th>
              </tr>
            </thead>
            <tbody>
              {filteredClients.map(c => (
                <tr key={c.caseId} className={`border-b border-white/5 transition-colors ${c.concluido ? 'bg-[rgba(99,203,182,0.03)]' : 'hover:bg-white/[0.02]'}`}>
                  <td className="py-2.5 px-3">
                    <p className="text-xs text-white/85">{c.nome}</p>
                    <p className="text-[9px] text-white/30">{c.email}</p>
                  </td>
                  <td className="py-2.5 px-3"><span className="text-xs font-mono text-white/55">{c.cpf}</span></td>
                  {EMPRESAS.map(emp => {
                    const baixa = c.baixas?.[emp.key];
                    const done = baixa?.status === 'baixado';
                    return (
                      <td key={emp.key} className="py-2.5 px-2 text-center">
                        {done ? (
                          <CheckCircle size={14} style={{color:'var(--rn-green)', margin:'0 auto'}} />
                        ) : (
                          <span className="w-3 h-3 rounded-full bg-white/10 block mx-auto" />
                        )}
                      </td>
                    );
                  })}
                  <td className="py-2.5 px-2 text-center">
                    <span className={`text-[10px] ${c.concluido ? 'badge-success' : c.baixadasCount > 0 ? 'badge-warning' : 'badge-neutral'}`}>
                      {c.concluido ? 'Concluido' : c.baixadasCount > 0 ? `${c.baixadasCount}/4` : 'Pendente'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Audit Log */}
      {data.auditLogs?.length > 0 && (
        <div className="glass-card">
          <h3 className="text-sm font-semibold text-white/70 mb-3">Historico / Auditoria</h3>
          <div className="space-y-2">
            {data.auditLogs.map((log, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <div className="w-1.5 h-1.5 rounded-full" style={{background:'var(--rn-green)'}} />
                <span className="text-white/60">{log.action}</span>
                {log.details && <span className="text-white/30">{JSON.stringify(log.details)}</span>}
                <span className="text-white/20 ml-auto">{log.timestamp ? new Date(log.timestamp).toLocaleDateString('pt-BR',{day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'}) : ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
